/**
 * Analytics helpers for the TA board's API layer.
 *
 * Split out of `taActionBoardApi.ts` to keep that file under the 500-line lint ceiling, but the
 * separation earns its place: this is the one place that decides what a board action is CALLED in
 * analytics, and the Usage Analytics catalogue is written against those names.
 */
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';

/**
 * Analytics event name for a board action, derived from the action TYPE rather than listed.
 *
 * Actions routed through `postAction` are named here so a new type is tracked the day it is added.
 * NOTE the limit that once caused a real bug: only `archive` and `move` go through `postAction`.
 * Every other action has its own endpoint and must be instrumented there with a literal name —
 * assuming otherwise left nine catalogued names with no emitter, and three actively-used tabs
 * reported as unused. `fr-book` → `ta_board_fr_book`.
 */
export function boardActionEvent(type: string): string {
  return `ta_board_${`${type}`.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
}

/**
 * Wrap an API call so it reports whether it SUCCEEDED, under a fixed event name.
 *
 * Reporting from beside the request is what keeps the event honest: an emit at the call site in the
 * UI fires on intent, so a failed action — or one behind an undo countdown the user cancels — would
 * be counted as having happened.
 *
 * Failures get their own name. Without one, a flow that fails every time is indistinguishable from
 * one nobody uses: both are simply absent from the data.
 */
export async function tracked<T>(
  event: string,
  run: () => Promise<T>,
  props: Record<string, string | number | boolean> = {}
): Promise<T> {
  try {
    const result = await run();
    // A RESOLVED promise is not a successful action. `postHolds`, `releaseHolds`, `askPanelists`
    // and `saveFinalRoundConfig` all answer `{ ok: false }` for a partial or refused result — the
    // UI shows a warning for exactly those — so emitting the success event on resolution alone
    // would count a failed action as a completed one. The result is returned either way, so the
    // warning UI is untouched.
    const ok = (result as { ok?: boolean })?.ok;
    if (ok === false) void logAnalyticsEvent(`${event}_failed`, { kind: 'not_ok' });
    else void logAnalyticsEvent(event, props);
    return result;
  } catch (error) {
    void logAnalyticsEvent(`${event}_failed`, {
      kind: error instanceof Error ? error.name : typeof error,
    });
    throw error;
  }
}
